import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Key, Copy, RefreshCw, Download, Settings, Trash2 } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

const UuidGenerator: React.FC = () => {
  const [uuids, setUuids] = useState<string[]>([]);
  const [count, setCount] = useState(1);
  const [version, setVersion] = useState<'v4' | 'v1'>('v4');
  const [format, setFormat] = useState<'standard' | 'uppercase' | 'no-hyphens' | 'braces'>('standard');

  useSEO({
    title: 'UUID Generator - Generate Unique Identifiers Online',
    description: 'Free online UUID generator. Generate UUIDs (v1, v4) in various formats. Bulk generation, copy to clipboard, and download options.',
    keywords: 'UUID generator, GUID generator, unique identifier, UUID v4, UUID v1, random UUID, generate UUID online',
    canonical: 'https://snap-tools.online/dev/uuid-generator'
  });

  const generateV4UUID = (): string => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  const generateV1UUID = (): string => {
    // Simplified v1 UUID generation (not RFC compliant but visually similar)
    const timestamp = Date.now();
    const random = Math.random().toString(16).substr(2, 8);
    const timeHex = timestamp.toString(16);
    
    return `${timeHex.substr(0, 8)}-${timeHex.substr(8, 4)}-1${timeHex.substr(12, 3)}-${random.substr(0, 4)}-${random.substr(4, 12)}`;
  };

  const formatUUID = (uuid: string): string => {
    switch (format) {
      case 'uppercase':
        return uuid.toUpperCase();
      case 'no-hyphens':
        return uuid.replace(/-/g, '');
      case 'braces':
        return `{${uuid}}`;
      default:
        return uuid;
    }
  };

  const generateUUIDs = () => {
    const newUuids: string[] = [];
    for (let i = 0; i < count; i++) {
      const uuid = version === 'v4' ? generateV4UUID() : generateV1UUID();
      newUuids.push(formatUUID(uuid));
    }
    setUuids(newUuids);
    toast.success(`Generated ${count} UUID${count > 1 ? 's' : ''}`);
  };

  const copyUUID = (uuid: string) => {
    navigator.clipboard.writeText(uuid).then(() => {
      toast.success('UUID copied to clipboard');
    });
  };

  const copyAllUUIDs = () => {
    const allUuids = uuids.join('\n');
    navigator.clipboard.writeText(allUuids).then(() => {
      toast.success('All UUIDs copied to clipboard');
    });
  };

  const downloadUUIDs = () => {
    const content = uuids.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'uuids.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('UUIDs downloaded');
  };

  const clearUUIDs = () => {
    setUuids([]);
    toast.success('UUIDs cleared');
  };

  // Auto-generate on mount
  useEffect(() => {
    generateUUIDs();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-8 sm:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 sm:mb-12"
        >
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-xl sm:rounded-2xl mb-4 sm:mb-6 shadow-lg">
            <Key className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#8438ff] mb-3 sm:mb-4">
            UUID Generator
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Generate unique identifiers (UUIDs) in various formats. Support for UUID v1 and v4 
            with bulk generation and multiple format options.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
          className="mb-6 sm:mb-8"
        />

        <div className="grid lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Settings Panel */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-5 sm:p-6 lg:p-8 hover:shadow-xl transition-all duration-300"
          >
            <div className="flex items-center mb-5 sm:mb-6">
              <div className="inline-flex items-center justify-center w-8 h-8 bg-[#8438ff]/10 rounded-lg mr-3">
                <Settings className="h-4 w-4 text-[#8438ff]" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">Settings</h3>
            </div>

            <div className="space-y-5 sm:space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Number of UUIDs
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={count}
                  onChange={(e) => setCount(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                  className="w-full p-3 sm:p-4 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#8438ff] focus:border-[#8438ff] transition-colors text-sm sm:text-base"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  UUID Version
                </label>
                <div className="space-y-3">
                  <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                    <input
                      type="radio"
                      value="v4"
                      checked={version === 'v4'}
                      onChange={(e) => setVersion(e.target.value as 'v4')}
                      className="mr-3 text-[#8438ff] focus:ring-[#8438ff]"
                    />
                    <div>
                      <span className="text-sm font-medium">UUID v4 (Random)</span>
                      <p className="text-xs text-gray-500 mt-1">Randomly generated identifiers</p>
                    </div>
                  </label>
                  <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                    <input
                      type="radio"
                      value="v1"
                      checked={version === 'v1'}
                      onChange={(e) => setVersion(e.target.value as 'v1')}
                      className="mr-3 text-[#8438ff] focus:ring-[#8438ff]"
                    />
                    <div>
                      <span className="text-sm font-medium">UUID v1 (Timestamp)</span>
                      <p className="text-xs text-gray-500 mt-1">Time-based identifiers</p>
                    </div>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Output Format
                </label>
                <select
                  value={format}
                  onChange={(e) => setFormat(e.target.value as any)}
                  className="w-full p-3 sm:p-4 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#8438ff] focus:border-[#8438ff] transition-colors text-sm sm:text-base bg-white"
                >
                  <option value="standard">Standard (lowercase)</option>
                  <option value="uppercase">Uppercase</option>
                  <option value="no-hyphens">No hyphens</option>
                  <option value="braces">With braces {}</option>
                </select>
              </div>

              <button
                onClick={generateUUIDs}
                className="w-full flex items-center justify-center px-4 py-3 sm:py-4 bg-gradient-to-r from-[#8438ff] to-[#7228ee] text-white rounded-lg sm:rounded-xl font-semibold hover:from-[#7328ee] hover:to-[#6218dd] transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Generate UUIDs
              </button>
            </div>
          </motion.div>

          {/* Generated UUIDs */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-5 sm:p-6 lg:p-8 hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-center justify-between mb-5 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center">
                  <Key className="h-5 w-5 mr-2 text-[#8438ff]" />
                  Generated UUIDs ({uuids.length})
                </h3>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={copyAllUUIDs}
                    disabled={uuids.length === 0}
                    className="flex items-center px-3 py-1.5 text-xs sm:text-sm text-gray-600 hover:text-[#8438ff] hover:bg-[#8438ff]/10 rounded-lg transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Copy all UUIDs"
                  >
                    <Copy className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5" />
                    Copy All
                  </button>
                  <button
                    onClick={downloadUUIDs}
                    disabled={uuids.length === 0}
                    className="flex items-center px-3 py-1.5 text-xs sm:text-sm text-gray-600 hover:text-[#8438ff] hover:bg-[#8438ff]/10 rounded-lg transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Download UUIDs"
                  >
                    <Download className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5" />
                    Download
                  </button>
                  <button
                    onClick={clearUUIDs}
                    disabled={uuids.length === 0}
                    className="flex items-center px-3 py-1.5 text-xs sm:text-sm text-gray-600 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Clear all UUIDs"
                  >
                    <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5" />
                    Clear
                  </button>
                </div>
              </div>

              {uuids.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {uuids.map((uuid, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="flex items-center justify-between p-3 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border hover:shadow-md transition-all duration-200"
                    >
                      <code className="text-sm sm:text-base font-mono text-gray-700 flex-1 mr-3 break-all">
                        {uuid}
                      </code>
                      <button
                        onClick={() => copyUUID(uuid)}
                        className="flex items-center px-2 py-1 text-xs text-gray-600 hover:text-[#8438ff] hover:bg-[#8438ff]/10 rounded transition-all duration-200"
                        title="Copy UUID"
                      >
                        <Copy className="h-3 w-3" />
                      </button>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Key className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-sm">
                    Click "Generate UUIDs" to create unique identifiers
                  </p>
                </div>
              )}
            </motion.div>

            {/* UUID Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-6 sm:mt-8 bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-5 sm:p-6 lg:p-8"
            >
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 flex items-center">
                <div className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 rounded-lg mr-3">
                  <Key className="h-4 w-4 text-blue-600" />
                </div>
                About UUIDs
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">UUID Versions</h4>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></div>
                      <div>
                        <strong>Version 1:</strong> Time-based with MAC address
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></div>
                      <div>
                        <strong>Version 4:</strong> Random or pseudo-random
                      </div>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Use Cases</h4>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li>• Database primary keys</li>
                    <li>• API request tracking</li>
                    <li>• File naming</li>
                    <li>• Session identifiers</li>
                    <li>• Distributed systems</li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
          className="mt-8 sm:mt-12"
        />
      </div>
    </div>
  );
};

export default UuidGenerator;
